import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import {
  BoldIcon,
  ItalicIcon,
  UnderlineIcon,
  AlignLeftIcon,
  AlignCenterIcon,
  AlignRightIcon,
  AlignJustifyIcon,
  LinkIcon,
  StrikethroughIcon,
  CodeIcon,
  ImageIcon,
  XmarkIcon,
} from '@revfluence/fresh-icons/regular/esm';
import { IndentDecreaseIcon, IndentIncreaseIcon } from 'lucide-react';
import 'react-quill/dist/quill.snow.css';
import { v4 as uuidv4 } from 'uuid';
import styles from './RichTextEditor.scss';

interface RichTextEditorProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  className?: string;
  excludeModules?: string[];
  initialValue?: string;
  enableAdvancedFeatures?: boolean;
}

interface CustomToolbarProps {
  id: string;
  excludeModules?: string[];
  enableAdvancedFeatures?: boolean;
  onImageUpload?: (file: File) => void;
}

const CustomToolbar: React.FC<CustomToolbarProps> = ({ id, excludeModules, enableAdvancedFeatures, onImageUpload }) => {
  const handleImageClick = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) => {
      const files = (e.target as HTMLInputElement).files;
      if (files && files[0] && onImageUpload) {
        onImageUpload(files[0]);
      }
    };
    input.click();
  };

  return (
    <div id={id} className="ql-toolbar flex items-center p-1 space-x-1">
      {/* Font Controls */}
      {enableAdvancedFeatures && (
        <>
          <span className="ql-formats">
            <select className="ql-font" defaultValue="Arial">
              <option value="Arial">Arial</option>
              <option value="Helvetica">Helvetica</option>
              <option value="Georgia">Georgia</option>
              <option value="Times New Roman">Times New Roman</option>
              <option value="Courier New">Courier New</option>
            </select>
            <select className="ql-size" defaultValue="14px">
              <option value="12px">12</option>
              <option value="14px">14</option>
              <option value="16px">16</option>
              <option value="18px">18</option>
              <option value="20px">20</option>
              <option value="24px">24</option>
            </select>
          </span>
          <div className="h-5 w-px bg-border mx-1" />
        </>
      )}

      {/* Text Formatting */}
      <span className="ql-formats">
        <button className="ql-bold p-1.5 hover:bg-grey-1 rounded" aria-label="Bold">
          <BoldIcon width={16} height={16} />
        </button>
        <button className="ql-italic p-1.5 hover:bg-grey-1 rounded" aria-label="Italic">
          <ItalicIcon width={16} height={16} />
        </button>
        <button className="ql-underline p-1.5 hover:bg-grey-1 rounded" aria-label="Underline">
          <UnderlineIcon width={16} height={16} />
        </button>
        {enableAdvancedFeatures && (
          <>
            <button className="ql-strike p-1.5 hover:bg-grey-1 rounded" aria-label="Strikethrough">
              <StrikethroughIcon width={16} height={16} />
            </button>
            <button className="ql-code p-1.5 hover:bg-grey-1 rounded" aria-label="Code">
              <CodeIcon width={16} height={16} />
            </button>
          </>
        )}
      </span>

      {/* Color Controls */}
      {enableAdvancedFeatures && (
        <>
          <div className="h-5 w-px bg-border mx-1" />
          <span className="ql-formats">
            <select className="ql-color">
              <option value="#000000" />
              <option value="#e60000" />
              <option value="#ff9900" />
              <option value="#ffff00" />
              <option value="#008a00" />
              <option value="#0066cc" />
              <option value="#9933ff" />
            </select>
            <select className="ql-background">
              <option value="#000000" />
              <option value="#e60000" />
              <option value="#ff9900" />
              <option value="#ffff00" />
              <option value="#008a00" />
              <option value="#0066cc" />
              <option value="#9933ff" />
            </select>
          </span>
        </>
      )}

      <div className="h-5 w-px bg-border mx-1" />

      {/* Alignment */}
      <span className="ql-formats">
        <button className="ql-align p-1.5 hover:bg-grey-1 rounded" value="" aria-label="Align Left">
          <AlignLeftIcon width={16} height={16} />
        </button>
        <button className="ql-align p-1.5 hover:bg-grey-1 rounded" value="center" aria-label="Align Center">
          <AlignCenterIcon width={16} height={16} />
        </button>
        <button className="ql-align p-1.5 hover:bg-grey-1 rounded" value="right" aria-label="Align Right">
          <AlignRightIcon width={16} height={16} />
        </button>
        {enableAdvancedFeatures && (
          <button className="ql-align p-1.5 hover:bg-grey-1 rounded" value="justify" aria-label="Justify">
            <AlignJustifyIcon width={16} height={16} />
          </button>
        )}
      </span>

      {/* Indentation */}
      {enableAdvancedFeatures && (
        <>
          <div className="h-5 w-px bg-border mx-1" />
          <span className="ql-formats">
            <button className="ql-indent p-1.5 hover:bg-grey-1 rounded" value="-1" aria-label="Decrease Indent">
              <IndentDecreaseIcon width={16} height={16} />
            </button>
            <button className="ql-indent p-1.5 hover:bg-grey-1 rounded" value="+1" aria-label="Increase Indent">
              <IndentIncreaseIcon width={16} height={16} />
            </button>
          </span>
        </>
      )}

      <div className="h-5 w-px bg-border mx-1" />

      {/* Lists */}
      <span className="ql-formats">
        <button className="ql-list p-1.5 hover:bg-grey-1 rounded" value="ordered" aria-label="Numbered List">
          1.
        </button>
        <button className="ql-list p-1.5 hover:bg-grey-1 rounded" value="bullet" aria-label="Bullet List">
          •
        </button>
      </span>

      {/* Media */}
      {!excludeModules?.includes('link') && (
        <>
          <div className="h-5 w-px bg-border mx-1" />
          <span className="ql-formats">
            <button className="ql-link p-1.5 hover:bg-grey-1 rounded" aria-label="Link">
              <LinkIcon width={16} height={16} />
            </button>
            {enableAdvancedFeatures && onImageUpload && (
              <button
                type="button"
                className="p-1.5 hover:bg-grey-1 rounded"
                aria-label="Insert Image"
                onClick={handleImageClick}
              >
                <ImageIcon width={16} height={16} />
              </button>
            )}
          </span>
        </>
      )}

      {/* Clean/Remove Formatting */}
      {enableAdvancedFeatures && (
        <>
          <div className="h-5 w-px bg-border mx-1" />
          <span className="ql-formats">
            <button className="ql-clean p-1.5 hover:bg-grey-1 rounded" aria-label="Remove Formatting">
              <XmarkIcon width={16} height={16} />
            </button>
          </span>
        </>
      )}
    </div>
  );
};

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value = '',
  onChange,
  placeholder = 'Write something...',
  className,
  excludeModules = [],
  initialValue,
  enableAdvancedFeatures = false,
}) => {
  const quillRef = useRef<any>(null);
  const [content, setContent] = useState(initialValue || value);

  useEffect(() => {
    if (initialValue) {
      setContent(initialValue);
    }
  }, [initialValue]);

  const toolbarId = useMemo(() => `custom-editor-toolbar-${uuidv4()}`, []);

  const ReactQuill = useMemo(() => (
    (typeof window === 'object' && typeof document === 'object')
      ? require('react-quill')
      : () => null
  ), []);

  const handleImageUpload = useCallback(async (file: File) => {
    // Placeholder for image upload functionality
    // In a real implementation, you would upload the file and get a URL back

    // For now, create a temporary URL for preview
    const tempUrl = URL.createObjectURL(file);

    // Insert image into Quill editor
    const quill = quillRef.current;
    if (quill) {
      const range = quill.getSelection(true);
      quill.insertEmbed(range.index, 'image', tempUrl, 'user');
      quill.setSelection(range.index + 1);
    }
  }, []);

  const handleChange = (newContent: string) => {
    setContent(newContent);
    onChange?.(newContent);
  };

  const modules = useMemo(() => ({
    toolbar: {
      container: `#${toolbarId}`,
      handlers: enableAdvancedFeatures ? {
        image: () => {
          // Custom image handler will be called from toolbar
        }
      } : undefined,
    },
    ...(enableAdvancedFeatures && {
      imageResize: {
        parchment: typeof window !== 'undefined' ? require('react-quill').Quill?.import('parchment') : null,
        modules: ['Resize', 'DisplaySize', 'Toolbar']
      }
    }),
  }), [toolbarId, enableAdvancedFeatures]);

  const formats = useMemo(() => {
    const baseFormats = ['bold', 'italic', 'underline', 'align', 'link', 'list'];

    if (enableAdvancedFeatures) {
      return [
        ...baseFormats,
        'font',
        'size',
        'color',
        'background',
        'strike',
        'code',
        'indent',
        'image',
        'clean'
      ];
    }

    return baseFormats;
  }, [enableAdvancedFeatures]);

  return (
    <div className={`${styles.RichTextEditor} ${className || ''}`}>
      <CustomToolbar
        id={toolbarId}
        excludeModules={excludeModules}
        enableAdvancedFeatures={enableAdvancedFeatures}
        onImageUpload={handleImageUpload}
      />
      <ReactQuill
        theme="snow"
        value={content}
        onChange={handleChange}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
        onSelectionChange={() => {
          const editor = document.querySelector(`#${toolbarId}`)?.nextElementSibling?.querySelector('.ql-editor');
          if (editor) {
            quillRef.current = (editor as any).__quill;
          }
        }}
      />
    </div>
  );
};

export default RichTextEditor;
